import {
  type InfrastructureCommand,
  InfrastructureCommandBuilder,
  type InfrastructureResponse,
  InfrastructureResponseBuilder,
} from '@teachino/shared-infrastructure';
import { CombinedError, useMutation, useQuery } from '@urql/vue';
import { computed, ref } from 'vue';
import * as z from 'zod';
import { deleteItemsMutation } from '../delete-items.mutation';
import { duplicateItemsMutation } from '../duplicate-items.mutation';
import { moveItemsMutation } from '../move-items.mutation';
import { reorderItemMutation } from '../reorder-item.mutation';
import { useLibraryDomain } from '../useLibraryDomain';
import type { Course } from './course.fragment';
import { editTopicGoalsMutation } from './edit-topic-goals.mutation';
import { renameTopicMutation } from './rename-topic.mutation';
import { convertTopicDetails } from './topic-details.fragment';
import { topicDetailsQuery } from './topic-details.query';
import type { SortOrder, TopicsFacade, ViewModel } from './topics.facade';

export class TopicsService implements TopicsFacade {
  private _renameTopic = useMutation(renameTopicMutation);
  private _editTopicGoals = useMutation(editTopicGoalsMutation);
  private _reorderItem = useMutation(reorderItemMutation);
  private _moveItems = useMutation(moveItemsMutation);
  private _duplicateItems = useMutation(duplicateItemsMutation);
  private _deleteItems = useMutation(deleteItemsMutation);

  private _topicId = ref<string | null>(null);
  private _courseId = ref<string | null>(null);
  private _sort = ref<'default' | 'alphabetically' | 'date'>('default');

  private _topicDetailsQuery = useQuery({
    query: topicDetailsQuery,
    variables: computed(() => {
      return {
        topicId: this._topicId.value ?? '',
        courseId: this._courseId.value,
      };
    }),
    pause: computed(() => this._topicId.value === null),
  });

  constructor() {
    /* RELOAD DATA ON NEW ITEMS CREATED VIA LEGACY API */
    const libraryDomain = useLibraryDomain();
    libraryDomain.onItemCreated(() => {
      this.reloadData();
    });
    libraryDomain.onItemVisibilityChanged(() => {
      this.reloadData();
    });
  }

  vm(): InfrastructureResponse<ViewModel> {
    return InfrastructureResponseBuilder.fromQuery(this._topicDetailsQuery)
      .withMapper((result) => {
        const topic = convertTopicDetails(result.topicContent);
        const selectedCourse =
          topic.courses.find((course) => course.id === this._courseId.value) ??
          null;

        // Add course to items
        const items: ViewModel['items'] = topic.items.map((item) => {
          const courseSettingsForSelectedCourse = item.courseSettings.find(
            (setting) => setting.id === this._courseId.value,
          );
          const course =
            courseSettingsForSelectedCourse && selectedCourse
              ? {
                  ...courseSettingsForSelectedCourse,
                  ...selectedCourse,
                }
              : null;
          const { id, name, icon, contentStatistics, createdAt, duration } =
            item;

          const interactiveAssignmentsStatus =
            result.interactiveAssignmentsStatusByTopic.find(
              (status) => status.materialId === id,
            ) ?? null;

          return {
            id,
            name,
            icon,
            course,
            duration,
            contentStatistics,
            createdAt,
            interactiveAssignmentsStatus,
          };
        });

        const res = {
          selectedCourse,
          topic,
          items: this.sortItems({ items, sortOrder: this._sort.value }),
        };
        return res;
      })
      .build();
  }

  renameTopic(id: string, name: string): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(this._renameTopic, {
      id,
      name,
    }).build();
  }

  editTopicGoals(id: string, goals: string): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(this._editTopicGoals, {
      id,
      goals,
    }).build();
  }

  reloadData(): void {
    this._topicDetailsQuery.executeQuery();
  }

  selectTopic(id: string): void {
    this._topicId.value = id;
    this.reloadData();
  }
  selectCourse(id: string | null): void {
    const isCourseLoaded = this.vm().data.value?.topic.courses.some(
      (c) => c.id === id,
    );
    if (!isCourseLoaded) {
      this.reloadData();
    }

    this._courseId.value = id;
  }

  selectSortOrder(sortOption: 'default' | 'alphabetically' | 'date') {
    if (sortOption === 'default') {
      this.reloadData();
    }
    this._sort.value = sortOption;
  }

  private sortItems(args: {
    items: ViewModel['items'];
    sortOrder: SortOrder;
  }): ViewModel['items'] {
    const sortAlphabetically = (items: ViewModel['items']) => {
      return items.sort((a, b) => {
        if (!a.name || !b.name) return 0;
        return a.name.localeCompare(b.name);
      });
    };
    const sortByDate = (items: ViewModel['items']) => {
      return items.sort((a, b) => {
        // Extract dates arrays
        const aDates = a.course?.dates ?? [];
        const bDates = b.course?.dates ?? [];

        // Check if arrays are empty and put them at the end
        if (aDates.length === 0 && bDates.length === 0) return 0;
        if (aDates.length === 0) return 1;
        if (bDates.length === 0) return -1;

        // Compare the first dates in the arrays
        const aFirstDate = new Date(aDates[0]);
        const bFirstDate = new Date(bDates[0]);
        return aFirstDate.getTime() - bFirstDate.getTime();
      });
    };
    const sortByUpdate = (items: ViewModel['items']) => {
      return items.sort((a, b) => {
        return b.createdAt.getTime() - a.createdAt.getTime();
      });
    };
    const sortByDefault = (items: ViewModel['items']) => items;
    const sortMap: Record<
      SortOrder,
      (items: ViewModel['items']) => ViewModel['items']
    > = {
      alphabetically: sortAlphabetically,
      date: sortByDate,
      default: sortByDefault,
      created: sortByUpdate,
    };

    return sortMap[args.sortOrder](args.items);
  }

  reorderItem(itemId: string, topicId: string, position: number) {
    return InfrastructureCommandBuilder.fromMutation(this._reorderItem, {
      itemReorderInput: {
        id: itemId,
        position,
        topicId,
      },
    }).build();
  }

  moveItems(fromTopicId: string, toTopicId: string, itemIds: string[]) {
    return InfrastructureCommandBuilder.fromMutation(this._moveItems, {
      multiTopicItemMoveInput: {
        targetTopicId: toTopicId,
        originTopicId: fromTopicId,
        itemIds,
        forceMove: true,
      },
    })
      .withSideeffect(() => {
        this.reloadData();
      })
      .withFetching((fetching) => computed(() => fetching.value))
      .build();
  }

  duplicateItems(
    toTopicId: string,
    itemIds: string[],
  ): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(this._duplicateItems, {
      duplicateItemsInput: {
        targetTopicId: toTopicId,
        itemIds,
      },
    })
      .withSideeffect(() => {
        this.reloadData();
      })
      .withFetching((fetching) => computed(() => fetching.value))
      .build();
  }

  deleteItems(
    topicId: string,
    itemIds: string[],
  ): InfrastructureCommand<void, { affectedCourses: Course[] }> {
    return InfrastructureCommandBuilder.fromMutation(this._deleteItems, {
      multiTopicItemRemoveInput: {
        itemIds,
        topicId,
        forceDelete: false,
      },
    })
      .withErrorMapper((error) => {
        return this.parseDeleteItemsError(error);
      })
      .withSideeffect(() => {
        this.reloadData();
      })
      .withFetching((fetching) => computed(() => fetching.value))
      .build();
  }

  private parseDeleteItemsError(error: CombinedError): {
    affectedCourses: Course[];
  } {
    if (error.graphQLErrors.length === 0) {
      throw new Error('Graphql Error thrown with 0-length Error array');
    }
    const [firstError] = error.graphQLErrors;
    const extensions = firstError.extensions;
    const extensionSchema = z.object({
      myExtensions: z.object({
        data: z.array(
          z.object({
            id: z.string().min(1),
            shortName: z.string().nullable(),
            name: z.string().min(1),
            foregroundColor: z.string().min(1),
            backgroundColor: z.string().min(1),
            __typename: z.literal('Course'),
            permissions: z.object({
              canAddCourseTopicInstance: z.boolean(),
              canEditCourseTopicInstance: z.boolean(),
              canRemoveCourseTopicInstance: z.boolean(),
            }),
          }),
        ),
      }),
    });
    const parsedExtensions = extensionSchema.safeParse(extensions);
    if (parsedExtensions.success) {
      const courses = parsedExtensions.data.myExtensions.data;
      const courseMap = new Map<string, Course>();
      courses.forEach((course) => courseMap.set(course.id, course));
      return { affectedCourses: [...courseMap.values()] };
    } else {
      console.error(parsedExtensions.error);
      return { affectedCourses: [] };
    }
  }

  confirmDeleteItems(
    topicId: string,
    itemIds: string[],
  ): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(this._deleteItems, {
      multiTopicItemRemoveInput: {
        itemIds,
        topicId,
        forceDelete: true,
      },
    })
      .withSideeffect(() => {
        this.reloadData();
      })
      .build();
  }
}
